import React from "react";
import { Modal, Table } from "antd";
import styled from "@emotion/styled";
import COLORS from "common/Colors";
import { dateInDetail } from "utils/Date";
import { useSelector } from "react-redux";
import { getLocations, getUserList } from "redux/selectors";
import { useTranslation } from "react-i18next";

const StyledModal = styled(Modal)`
  width: 100% !important;
  max-width: 700px;
  .ant-modal-header {
    text-transform: uppercase;
    max-width: 95%;
  }
`;

const StyledTable = styled(Table)`
  .ant-table-content {
    overflow-x: auto;
    max-height: 500px;
    white-space: nowrap;
    border: 1px solid #00000024;
    border-radius: 10px;
  }
  .ant-table-cell {
    padding: 10px;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    color: ${COLORS.SECONDARY_BLACK};
  }
  .non-white {
    background: #f9f9f9;
  }
  .ant-table-thead > tr > th::before {
    display: none !important;
  }

  .ant-table-cell-row-hover {
    background: #e3e8fc !important;
  }
`;

const geoCache = new Map();
const geoInFlight = new Map();

const fetchAddress = (lat, lng) => {
  const key = `${lat},${lng}`;

  if (geoCache.has(key)) return Promise.resolve(geoCache.get(key));
  if (geoInFlight.has(key)) return geoInFlight.get(key);

  const promise = fetch(
    `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
    { headers: { "Accept-Language": "en" } }
  )
    .then((r) => r.json())
    .then((d) => {
      const a = d?.address;
      const label =
        a?.office ||
        a?.building ||
        a?.amenity ||
        a?.road ||
        a?.suburb ||
        a?.city ||
        d?.display_name?.split(",").slice(0, 2).join(", ") ||
        key;
      geoCache.set(key, label);
      geoInFlight.delete(key);
      return label;
    })
    .catch(() => {
      geoCache.set(key, key);
      geoInFlight.delete(key);
      return key;
    });

  geoInFlight.set(key, promise);
  return promise;
};

const useGeoAddress = (lat, lng) => {
  const key = lat && lng ? `${lat},${lng}` : null;

  const [address, setAddress] = React.useState(() =>
    key ? geoCache.get(key) ?? null : null
  );
  const [loading, setLoading] = React.useState(() =>
    key ? !geoCache.has(key) : false
  );

  React.useEffect(() => {
    if (!key) return;

    if (geoCache.has(key)) {
      setAddress(geoCache.get(key));
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetchAddress(lat, lng).then((label) => {
      if (!cancelled) {
        setAddress(label);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return { address, loading };
};

const LocationCell = React.memo(({ lat, lng, hasother, locationName }) => {
  const hasCoords =
    lat != null &&
    lat !== "" &&
    lat !== undefined &&
    lng != null &&
    lng !== "" &&
    lng !== undefined;

  const { address, loading } = useGeoAddress(
    hasCoords ? lat : null,
    hasCoords ? lng : null
  );

  if (!hasCoords) return <span>-</span>;
  if (loading)
    return <span style={{ color: "#888", fontSize: 12 }}>Resolving…</span>;

  const mapUrl = `http://maps.google.com/maps/?q=${lat},${lng}`;
  return (
    <>
      <a href={mapUrl} target="_blank" rel="noreferrer">
        {hasother ? address : locationName}
      </a>
      <br />
      <span style={{ fontSize: "10px", color: "#ccc" }}>
        {lat}, {lng}
      </span>
    </>
  );
});

function AttendanceStaffList({
  displayModal,
  toggleModal,
  title,
  list,
  name,
  reportType,
}) {
  const { t } = useTranslation();
  const userList = useSelector(getUserList);
  const locationList = useSelector(getLocations) || [];

  const columns = [
    {
      title: t("Name"),
      dataIndex:
        reportType === "Leave"
          ? "name"
          : reportType === "Absent"
          ? "fullname"
          : reportType === "WFH" || name === "Time Off in Lieu"
          ? "userId"
          : "fullname",
      key: "fullName",
      render: (text, record) => {
        return "Time Off in Lieu" === name || reportType === "WFH"
          ? userList?.find((val) => val?.userid === record?.userId)?.fullname ||
              "-"
          : text;
      },
    },
  ];

  if (reportType === "Leave") {
    columns.push({
      title: "Department",
      dataIndex: "departmentName",
      key: "departmentName",
      align: "center",
    });
    columns.push({
      title: t("Leave Category"),
      dataIndex: "leave_category_name",
      key: "leave_category_name",
      align: "center",
    });
    columns.push({
      title: t("Start date"),
      dataIndex: "start_date",
      key: "start_date",
      align: "center",
      render: (start_date) => {
        if (start_date === "") return "-";
        const { date: day_th, shortMonthName, year } = dateInDetail(start_date);
        return `${day_th} ${shortMonthName} ${year}`;
      },
    });
    columns.push({
      title: t("End date"),
      dataIndex: "end_date",
      key: "end_date",
      align: "center",
      render: (end_date) => {
        if (end_date === "") return "-";
        const { date: day_th, shortMonthName, year } = dateInDetail(end_date);
        return `${day_th} ${shortMonthName} ${year}`;
      },
    });
    columns.push({
      title: t("No.of leave days"),
      dataIndex: "number_of_days",
      key: "number_of_days",
      align: "center",
    });
  } else if (reportType === "Present" && name !== "Time Off in Lieu") {
    if (name === "onTime") {
      columns.push({
        title: t("Station Name"),
        dataIndex: "loginlocationname",
        key: "loginlocationname",
        align: "center",
      });
      columns.push({
        title: <div>{t("Google Location")}</div>,
        dataIndex: "location",
        key: "loginlatitude",
        width: "50%",
        align: "center",
        render: (_, record) => {
          const findLocation = locationList?.find(
            (item) => item?.location_id === String(record?.loginlocationid)
          );
          console.log("findLocation", findLocation);
          const hasother =
            findLocation?.login_from_anywhere === 1 ? true : false;
          console.log("hasother", hasother);
          return (
            <LocationCell
              lat={record.loginlatitude}
              lng={record.loginlongitude}
              hasother={hasother}
              locationName={record.loginlocationname}
            />
          );
        },
      });
      columns.push({
        title: t("Check-In Time"),
        dataIndex: "checkintime",
        key: "checkintime",
        align: "center",
      });
    } else if (name === "Late") {
      columns.push({
        title: t("Station Name"),
        dataIndex: "loginlocationname",
        key: "loginlocationname",
        align: "center",
      });
      columns.push({
        title: <div>{t("Google Location")}</div>,
        dataIndex: "location",
        key: "loginlatitude",
        width: "50%",
        align: "center",
        render: (_, record) => {
          const findLocation = locationList?.find(
            (item) => item?.location_id === String(record?.loginlocationid)
          );
          console.log("findLocation", findLocation);
          const hasother =
            findLocation?.login_from_anywhere === 1 ? true : false;
          console.log("hasother", hasother);
          return (
            <LocationCell
              lat={record.loginlatitude}
              lng={record.loginlongitude}
              hasother={hasother}
              locationName={record.loginlocationname}
            />
          );
        },
      });
      columns.push({
        title: t("Check-In Time"),
        dataIndex: "checkintime",
        key: "checkintime",
        align: "center",
      });
    } else {
      columns.push({
        title: t("Check-In Time"),
        dataIndex: "checkintime",
        key: "checkintime",
        width: "50%",
        align: "center",
        render: (checkinTime) => {
          const { time24 } = dateInDetail(checkinTime);
          return `${time24}`;
        },
      });
    }
  } else {
    columns.push({
      title: t("Email ID"),
      dataIndex:
        reportType === "WFH" || name === "Time Off in Lieu"
          ? "userId"
          : "userid",
      key: "userid",
      align: "center",
    });
  }

  return (
    <>
      <StyledModal
        title={name === "onTime" ? t("On time check-in report") : title}
        visible={displayModal}
        onCancel={toggleModal}
        footer={null}
        destroyOnClose
      >
        <StyledTable
          dataSource={list}
          columns={columns}
          rowClassName={(record, index) => (index % 2 === 0 ? "" : "non-white")}
          rowKey="user_id"
          pagination={false}
        />
      </StyledModal>
    </>
  );
}

export default AttendanceStaffList;
